import i18next from 'i18next';
import type { ComponentProps } from 'react';
import type { MarketCode } from '../../../../@types/Token';
import type { DetailedVehicle } from '../../../../data/vehicles/useDetailedVehicle';
import type InputText from '../../../../styled-components/components/InputText';
import type ShallowPanel from '../../../../styled-components/components/Panel/ShallowPanel';
import { vin } from '../../../../utils/zod-schemas/vinNumber';
import { registrationNumber } from './validationMethods';

export type CreateVehiclePanels = 'SELECT' | 'REGISTERED' | 'OEM' | 'ORDER';

export const getVehiclePanelProps = (
  panel: CreateVehiclePanels,
  navigate: (panel: CreateVehiclePanels) => void,
  showTemplateSelect: boolean,
  closeTemplateSelect: () => void,
  marketCode: MarketCode,
): Omit<ComponentProps<typeof ShallowPanel>, 'onClose'> => {
  if (showTemplateSelect) {
    return {
      title: i18next.t('selectTemplate', {
        ns: 'createVehiclePanel',
      }),
      onNavBack: {
        onClick: closeTemplateSelect,
        label: i18next.t('selectTemplate', {
          ns: 'createVehiclePanel',
        }),
      },
    };
  }

  const onClick = () => navigate('SELECT');
  switch (panel) {
    case 'SELECT':
      return {
        title: i18next.t('addVehicle', { ns: 'createVehiclePanel' }),
      };
    case 'REGISTERED':
      return {
        title: i18next.t('addVehicle', { ns: 'createVehiclePanel' }),
        onNavBack:
          marketCode === 'NO'
            ? undefined
            : { onClick, label: i18next.t('addVehicle', { ns: 'createVehiclePanel' }) },
      };
    case 'OEM':
      return {
        title: i18next.t('addVehicle', { ns: 'createVehiclePanel' }),
        onNavBack:
          marketCode === 'NO'
            ? undefined
            : { onClick, label: i18next.t('addVehicle', { ns: 'createVehiclePanel' }) },
      };
    case 'ORDER':
      return {
        title: i18next.t('orderVehicle', { ns: 'createVehiclePanel' }),
        onNavBack:
          marketCode === 'NO'
            ? undefined
            : { onClick, label: i18next.t('orderVehicle', { ns: 'createVehiclePanel' }) },
      };
  }
};

export const getRegVinLookupFailureState = (
  lookupFailureState: ComponentProps<typeof InputText>['state'],
  error?: boolean,
  detailedVehicle?: DetailedVehicle,
): ComponentProps<typeof InputText>['state'] => {
  if (detailedVehicle) {
    return {
      type: 'success',
      message: `${detailedVehicle?.manufacturer} ${detailedVehicle?.salesName} ${
        detailedVehicle?.equipmentLevel ? detailedVehicle?.equipmentLevel : ''
      } (${detailedVehicle?.modelYear})`,
    };
  } else if (error) {
    return lookupFailureState;
  }
};

export const separateRegVin = ({
  marketCode,
  regOrVin,
}: {
  marketCode?: MarketCode;
  regOrVin?: string;
}) => {
  const validVin = vin.safeParse(regOrVin);
  const validReg = registrationNumber.safeParse({
    marketCode,
    registrationNumber: regOrVin,
  });

  return {
    registrationNumber: validReg.data?.registrationNumber,
    vin: validVin.data,
  };
};
